import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { PaymentAccountsService } from 'core/services/payment-accounts.service';
import { KorviuNotificationService } from 'core/services/korviu-notification.service';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { TaxDocument } from 'shared/types/payment-account.interface';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { buildTaxDocumentReadonlyRows } from '../tax-documentation-modal/tax-documentation-contract.helpers';

interface IRemoveTaxDocumentationModalData {
  taxDocument: TaxDocument;
}

@Component({
  selector: 'app-remove-tax-documentation-modal',
  imports: [NzModalModule, TranslatePipe, KorviuButtonComponent],
  templateUrl: './remove-tax-documentation-modal.component.html',
  styleUrl: './remove-tax-documentation-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RemoveTaxDocumentationModalComponent {
  readonly #modalData = inject<IRemoveTaxDocumentationModalData>(NZ_MODAL_DATA);
  readonly #modalRef = inject<NzModalRef<RemoveTaxDocumentationModalComponent>>(NzModalRef);
  readonly #paymentAccountsService = inject(PaymentAccountsService);
  readonly #korviuNotificationService = inject(KorviuNotificationService);
  readonly #translateService = inject(TranslateService);
  readonly #destroyRef = inject(DestroyRef);

  readonly isLoading = signal(false);
  readonly documentRows = buildTaxDocumentReadonlyRows(this.#modalData.taxDocument, key =>
    this.#translateService.instant(key)
  );

  onCancelClick(): void {
    this.#modalRef.destroy(false);
  }

  onRemoveClick(): void {
    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    this.#paymentAccountsService
      .removeMyTaxDocument()
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: () => {
          this.#korviuNotificationService.showSuccess(
            this.#translateService.instant('profile.taxDocumentationSuccessfullyRemoved')
          );
          this.#modalRef.destroy(true);
        },
        error: () => {
          this.#korviuNotificationService.showError(
            this.#translateService.instant('profile.taxDocumentationRemoveError')
          );
        }
      });
  }
}
